/**
 * @module footnote/footnoteui
 */

import ContainerElement from '@ckeditor/ckeditor5-engine/src/view/containerelement';
import Position from '@ckeditor/ckeditor5-engine/src/view/position';
import { Plugin } from 'ckeditor5/src/core';
import { ClickObserver } from 'ckeditor5/src/engine';
import { ButtonView, clickOutsideHandler, ContextualBalloon } from 'ckeditor5/src/ui';
import { isWidget } from 'ckeditor5/src/widget';
import footnoteIcon from '../theme/icons/footnote.svg';
import FootnoteActionsView from './ui/footnoteactionsview';
import FootnoteFormView from './ui/footnoteformview';
import { isFootnoteElement } from './utils';

/**
 * The footnote UI plugin. It introduces the `'footnote'` buttons.
 *
 * It uses the {@link module:ui/panel/balloon/contextualballoon~ContextualBalloon contextual balloon plugin}.
 *
 * @extends {Plugin}
 */
export default class FootnoteUI extends Plugin {
	/**
	 * @inheritDoc
	 */
	static get requires() {
		return [ ContextualBalloon ];
	}

	/**
	 * @inheritDoc
	 */
	static get pluginName() {
		return 'FootnoteUI';
	}

	/**
	 * @inheritDoc
	 */
	init() {
		const editor = this.editor;

		editor.editing.view.addObserver( ClickObserver );

		this.actionsView = this._createActionsView();
		this.formView = this._createFormView();

		/**
		 * The contextual balloon plugin instance.
		 *
		 * @private
		 * @type {ContextualBalloon}
		 */
		this._balloon = editor.plugins.get( ContextualBalloon );

		this._createToolbarFootnoteButton();
		this._enableUserBalloonInteractions();
	}

	/**
	 * @inheritDoc
	 */
	destroy() {
		super.destroy();
		this.formView.destroy();
	}

	/**
	 * Creates the {@link module:footnote/ui/footnoteactionsview~FootnoteActionsView} instance.
	 *
	 * @private
	 * @returns {FootnoteActionsView} The footnote actions view instance.
	 */
	_createActionsView() {
		const editor = this.editor;
		const actionsView = new FootnoteActionsView( editor.locale );
		const footnoteCommand = editor.commands.get( 'footnote' );

		actionsView.bind( 'value' ).to( footnoteCommand );
		actionsView.editButtonView.bind( 'isEnabled' ).to( footnoteCommand );

		// Execute footnote command after clicking on the "Edit" button.
		this.listenTo( actionsView, 'edit', () => {
			this._addFormView();
		} );

		// Close the panel on esc key press when the **actions have focus**.
		actionsView.keystrokes.set( 'Esc', ( data, cancel ) => {
			this._hideUI();
			cancel();
		} );

		return actionsView;
	}

	/**
	 * Creates the {@link module:footnote/ui/footnoteformview~FootnoteFormView} instance.
	 *
	 * @private
	 * @returns {FootnoteFormView} The footnote form instance.
	 */
	_createFormView() {
		const editor = this.editor;
		const footnoteCommand = editor.commands.get( 'footnote' );

		const formView = new FootnoteFormView( editor.locale );

		formView.valueInputView.bind( 'isReadOnly' ).to( footnoteCommand, 'isEnabled', value => !value );
		formView.saveButtonView.bind( 'isEnabled' ).to( footnoteCommand );

		// Execute footnote command after clicking the "Save" button.
		this.listenTo( formView, 'submit', () => {
			const newAttributes = {};
			const oldValue = footnoteCommand.value || '';
			const newValue = formView.valueInputView.fieldView.element.value;
			if ( oldValue != newValue ) {
				newAttributes.value = newValue;
			}

			if ( newAttributes.value != null ) {
				editor.execute( 'footnote', newAttributes );
			}
			this._closeFormView();
		} );

		// Hide the panel after clicking the "Cancel" button.
		this.listenTo( formView, 'cancel', () => {
			this._closeFormView();
		} );

		// Close the panel on esc key press when the **form has focus**.
		formView.keystrokes.set( 'Esc', ( data, cancel ) => {
			this._closeFormView();
			cancel();
		} );

		return formView;
	}

	/**
	 * Creates a toolbar Footnote button. Clicking this button will show
	 * a {@link #_balloon} attached to the selection.
	 *
	 * @private
	 */
	_createToolbarFootnoteButton() {
		const editor = this.editor;
		const footnoteCommand = editor.commands.get( 'footnote' );
		const t = editor.t;

		editor.ui.componentFactory.add( 'footnote', locale => {
			const button = new ButtonView( locale );

			button.isEnabled = true;
			button.label = t( 'Nota de rodapé' );
			button.icon = footnoteIcon;
			button.tooltip = true;
			button.isToggleable = true;

			// Bind button to the command.
			button.bind( 'isEnabled' ).to( footnoteCommand, 'isEnabled' );
			button.bind( 'isOn' ).to( footnoteCommand, 'value', value => !!value );

			// Show the panel on button click.
			this.listenTo( button, 'execute', () => this._showUI( true ) );

			return button;
		} );
	}

	/**
	 * Attaches actions that control whether the balloon panel containing the
	 * {@link #formView} is visible or not.
	 *
	 * @private
	 */
	_enableUserBalloonInteractions() {
		const viewDocument = this.editor.editing.view.document;

		// Handle click on view document and show panel when selection is placed inside the footnote element.
		// Keep panel open until selection will be inside the same footnote element.
		this.listenTo( viewDocument, 'click', () => {
			const elementFootnote = this._getSelectedFootnoteElement();

			if ( elementFootnote ) {
				// Then show panel but keep focus inside editor editable.
				this._showUI();
			}
		} );

		// Focus the form if the balloon is visible and the Tab key has been pressed.
		this.editor.keystrokes.set( 'Tab',
			( data, cancel ) => {
				if ( this._areActionsVisible && !this.actionsView.focusTracker.isFocused ) {
					this.actionsView.focus();
					cancel();
				}
			}, {
			priority: 'high'
		} );

		// Close the panel on the Esc key press when the editable has focus and the balloon is visible.
		this.editor.keystrokes.set( 'Esc', ( data, cancel ) => {
			if ( this._isUIVisible ) {
				this._hideUI();
				cancel();
			}
		} );

		// Close on click outside of balloon panel element.
		clickOutsideHandler( {
			emitter: this.formView,
			activator: () => this._isUIVisible,
			contextElements: [ this._balloon.view.element ],
			callback: () => this._hideUI()
		} );
	}

	/**
	 * Adds the {@link #actionsView} to the {@link #_balloon}.
	 *
	 * @protected
	 */
	_addActionsView() {
		if ( this._areActionsInPanel ) {
			return;
		}

		this._balloon.add( {
			view: this.actionsView,
			position: this._getBalloonPositionData()
		} );
	}

	/**
	 * Adds the {@link #formView} to the {@link #_balloon}.
	 *
	 * @protected
	 */
	_addFormView( initialValue = '' ) {
		if ( this._isFormInPanel ) {
			return;
		}

		const footnoteCommand = this.editor.commands.get( 'footnote' );

		this.formView.disableCssTransitions();

		this._balloon.add( {
			view: this.formView,
			position: this._getBalloonPositionData()
		} );

		if ( this._balloon.visibleView === this.formView ) {
			this.formView.valueInputView.fieldView.select();
		}

		this.formView.enableCssTransitions();

		this.formView.valueInputView.fieldView.element.value = footnoteCommand.value || initialValue;
	}

	/**
	 * Removes the {@link #formView} from the {@link #_balloon}.
	 *
	 * @protected
	 */
	_closeFormView() {
		const footnoteCommand = this.editor.commands.get( 'footnote' );

		if ( footnoteCommand.value != null ) {
			this._removeFormView();
		} else {
			this._hideUI();
		}
	}

	/**
	 * Removes the {@link #formView} from the {@link #_balloon}.
	 *
	 * @protected
	 */
	_removeFormView() {
		if ( this._isFormInPanel ) {
			// Blur the input element before removing it from DOM to prevent issues in some browsers.
			// See https://github.com/ckeditor/ckeditor5/issues/1501.
			this.formView.saveButtonView.focus();

			this._balloon.remove( this.formView );

			// Because the form has an input which has focus, the focus must be brought back
			// to the editor. Otherwise, it would be lost.
			this.editor.editing.view.focus();
		}
	}

	/**
	 * Shows the right kind of the UI for current state of the command. It's either
	 * {@link #formView} or {@link #actionsView}.
	 *
	 * @param {Boolean} forceVisible
	 * @private
	 */
	_showUI( forceVisible = false ) {
		// When there's no footnote under the selection, go straight to the editing UI.
		if ( !this._getSelectedFootnoteElement() ) {
			this._addActionsView();

			// Be sure panel with footnote is visible.
			if ( forceVisible ) {
				this._balloon.showStack( 'main' );
			}

			this._addFormView( Array.from( this.editor.model.document.selection.getFirstRange().getItems() ).map( v => v.data ).join( '' ) );
		}
		// If there's a footnote under the selection...
		else {
			// Go to the editing UI if actions are already visible.
			if ( this._areActionsVisible ) {
				this._addFormView();
			}
			// Otherwise display just the actions UI.
			else {
				this._addActionsView();
			}

			// Be sure panel with footnote is visible.
			if ( forceVisible ) {
				this._balloon.showStack( 'main' );
			}
		}

		// Begin responding to ui#update once the UI is added.
		this._startUpdatingUI();
	}

	/**
	 * Removes the {@link #formView} from the {@link #_balloon}.
	 *
	 * See {@link #_addFormView}, {@link #_addActionsView}.
	 *
	 * @protected
	 */
	_hideUI() {
		if ( !this._isUIInPanel ) {
			return;
		}

		const editor = this.editor;

		this.stopListening( editor.ui, 'update' );
		this.stopListening( this._balloon, 'change:visibleView' );

		// Make sure the focus always gets back to the editable _before_ removing the focused form view.
		// Doing otherwise causes issues in some browsers. See https://github.com/ckeditor/ckeditor5-link/issues/193.
		editor.editing.view.focus();

		// Remove form first because it's on top of the stack.
		this._removeFormView();

		// Then remove the actions view because it's beneath the form.
		this._balloon.remove( this.actionsView );
	}

	/**
	 * Makes the UI react to the {@link module:core/editor/editorui~EditorUI#event:update} event to
	 * reposition itself when the editor ui should be refreshed.
	 *
	 * See: {@link #_hideUI} to learn when the UI stops reacting to the `update` event.
	 *
	 * @protected
	 */
	_startUpdatingUI() {
		const editor = this.editor;
		const viewDocument = editor.editing.view.document;

		let prevSelectedFootnote = this._getSelectedFootnoteElement();
		let prevSelectionParent = getSelectionParent();

		const update = () => {
			const selectedFootnote = this._getSelectedFootnoteElement();
			const selectionParent = getSelectionParent();

			// Hide the panel if:
			//
			// * the selection went out of the EXISTING footnote element. E.g. user moved the caret out
			//   of the footnote,
			// * the selection went to a different parent when creating a NEW footnote. E.g. someone
			//   else modified the document.
			// * the selection has expanded (e.g. displaying footnote actions then pressing SHIFT+Right arrow).
			//
			// Note: #_getSelectedFootnoteElement will return a footnote for a non-collapsed selection only
			// when fully selected.
			if ( ( prevSelectedFootnote && !selectedFootnote ) ||
				( !prevSelectedFootnote && selectionParent !== prevSelectionParent ) ) {
				this._hideUI();
			}
			// Update the position of the panel when:
			//  * footnote panel is in the visible stack
			//  * the selection remains in the original footnote element,
			//  * there was no footnote element in the first place, i.e. creating a new footnote
			else if ( this._isUIVisible ) {
				// If still in a footnote element, simply update the position of the balloon.
				// If there was no footnote (e.g. inserting one), the balloon must be moved
				// to the new position in the editing view (a new native DOM range).
				this._balloon.updatePosition( this._getBalloonPositionData() );
			}

			prevSelectedFootnote = selectedFootnote;
			prevSelectionParent = selectionParent;
		};

		function getSelectionParent() {
			return viewDocument.selection.focus.getAncestors()
				.reverse()
				.find( node => node.is( 'element' ) );
		}

		this.listenTo( editor.ui, 'update', update );
		this.listenTo( this._balloon, 'change:visibleView', update );
	}

	/**
	 * Returns true when {@link #formView} is in the {@link #_balloon}.
	 *
	 * @readonly
	 * @protected
	 * @type {Boolean}
	 */
	get _isFormInPanel() {
		return this._balloon.hasView( this.formView );
	}

	/**
	 * Returns true when {@link #actionsView} is in the {@link #_balloon}.
	 *
	 * @readonly
	 * @protected
	 * @type {Boolean}
	 */
	get _areActionsInPanel() {
		return this._balloon.hasView( this.actionsView );
	}

	/**
	 * Returns true when {@link #actionsView} is in the {@link #_balloon} and it is
	 * currently visible.
	 *
	 * @readonly
	 * @protected
	 * @type {Boolean}
	 */
	get _areActionsVisible() {
		return this._balloon.visibleView === this.actionsView;
	}

	/**
	 * Returns true when {@link #actionsView} or {@link #formView} is in the {@link #_balloon}.
	 *
	 * @readonly
	 * @protected
	 * @type {Boolean}
	 */
	get _isUIInPanel() {
		return this._isFormInPanel || this._areActionsInPanel;
	}

	/**
	 * Returns true when {@link #actionsView} or {@link #formView} is in the {@link #_balloon} and it is
	 * currently visible.
	 *
	 * @readonly
	 * @protected
	 * @type {Boolean}
	 */
	get _isUIVisible() {
		const visibleView = this._balloon.visibleView;

		return visibleView == this.formView || this._areActionsVisible;
	}

	/**
	 * Returns positioning options for the {@link #_balloon}. They control the way the balloon is attached
	 * to the target element or selection.
	 *
	 * If the selection is collapsed and inside a footnote element, the panel will be attached to the
	 * entire footnote element. Otherwise, it will be attached to the selection.
	 *
	 * @private
	 * @returns {Options}
	 */
	_getBalloonPositionData() {
		const view = this.editor.editing.view;
		const viewDocument = view.document;
		const target = () => {
			const targetFootnote = this._getSelectedFootnoteElement();

			return targetFootnote ?
				// When selection is inside footnote element, then attach panel to this element.
				view.domConverter.mapViewToDom( targetFootnote ) :
				// Otherwise attach panel to the selection.
				view.domConverter.viewRangeToDom( viewDocument.selection.getFirstRange() );
		};

		return { target };
	}

	/**
	 * Returns the footnote under the  editing view's selection or `null` if there is none.
	 *
	 * **Note**: For a non–collapsed selection the footnote element is only returned when **fully**
	 * selected and the **only** element within the selection boundaries.
	 *
	 * @private
	 * @returns {ContainerElement}
	 */
	_getSelectedFootnoteElement() {
		const view = this.editor.editing.view;
		const selection = view.document.selection;
		const selectedElement = selection.getSelectedElement();

		// The selection is collapsed or some widget is selected (especially inline widget).
		if ( selectedElement && isWidget( selectedElement ) && isFootnoteElement( selectedElement ) ) {
			return selectedElement;
		} else if ( selection.isCollapsed || selectedElement && isWidget( selectedElement ) ) {
			return findFootnoteElementAncestor( selection.getFirstPosition() );
		} else {
			// The range for fully selected footnote is usually anchored in adjacent text nodes.
			// Trim it to get closer to the actual footnote element.
			const range = selection.getFirstRange().getTrimmed();
			const startFootnote = findFootnoteElementAncestor( range.start );
			const endFootnote = findFootnoteElementAncestor( range.end );

			if ( !startFootnote || startFootnote != endFootnote ) {
				return null;
			}

			// Check if the footnote element is fully selected.
			if ( view.createRangeIn( startFootnote ).getTrimmed().isEqual( range ) ) {
				return startFootnote;
			} else {
				return null;
			}
		}
	}
}

/**
 * Returns a footnote element if there's one among the ancestors of the provided `Position`.
 *
 * @param {Position} position
 * @returns
 */
function findFootnoteElementAncestor( position ) {
	return position.getAncestors().find( ancestor => isFootnoteElement( ancestor ) );
}
